import { Client } from "@/Common/Api/Client";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { Guid } from "@/Common/Models/Guid";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { PatientExternalIdentifier } from "@/Common/Models/Patient/PatientExternalIdentifier";
import { PopupModel } from "@/Common/Models/UI/PopupModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { AdditionalSignOffInformation, ILinkingPopupParams, LinkingPopup } from "./LinkingPopup";

class LinkingPopupService {
    public static async promptForAdditionalSignOffInformation(
        patientContactId: string,
        EncounterSiteId: Guid,
        EncounterAppointmentId: string,
        EncounterSuggestedAppointmentLinkId: string
    ): Promise<AdditionalSignOffInformation> {
        const defaultInfo = new AdditionalSignOffInformation(
            {
                appointmentId: EncounterAppointmentId
            }
        );

        // patient external Id is required
        if (cacheStore.ClinicSettings.RequirePatientExternalIdToSignNote) {
            if (EncounterSiteId == null) {
                throw "EncounterSiteId is required for note approval.";
            }

            let patientModel = patientStore.PatientModels[patientContactId];
            if (patientModel == null) {
                const patientResponse = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, {
                    IndividualId: patientContactId,
                    IncludeRelatedRecalls: false,
                    IncludeRelationships: false
                });
                patientModel = MyleModelsPatientPatient.FromResponseData(patientResponse.data);
            }

            // check if user must link patient first
            const externalIdentifier = patientModel.GetVisibleExternalIdentifersForSiteId(EncounterSiteId);

            // Exit approval flow when no patient Id is found and patient is not a test patient
            if (!externalIdentifier.any() && !patientModel.PatientResponse.IsTest) {
                if (cacheStore.ClinicSettings.EnableExternalPatientSearch) {
                    // Fallback by searching externally
                    sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayExternalSearch, {
                        identifier: PatientExternalIdentifier.GetExternalSearchDefaultSearchForSiteId(patientModel, EncounterSiteId),
                        siteId: EncounterSiteId,
                        patientContactId: patientModel.IndividualResponse.Contact_id,
                        callback: () => patientStore.ActionsProfile.loadPatient(patientModel.IndividualResponse.Contact_id)
                    });
                }
                else {
                    // Show popup asking to call an agent of the external system for the patient to get a Myle id related to the site
                    // *Push events from the external system
                    sessionStore.showPopup(
                        new PopupModel({
                            title: cacheStore.GetString("WarningLabel"),
                            popupClass: "dsq-nip-popup-large dsq-nip-popup-breakspaces",
                            content: cacheStore.GetString("MedicalNotePatientLinkIsRequiredWarning"),
                            showOk: false,
                            showCancel: true,
                            cancelLabel: cacheStore.GetString("CloseLabel")
                        })
                    );
                }

                // User must click approve again after the patient reloaded from external search
                throw "Patient link required";
            }
        }

        let promptParams = {} as ILinkingPopupParams;

        // appointmentLink is required
        if (cacheStore.ClinicSettings.RequireAppointmentLinkToSignNote && !defaultInfo.appointmentId) {
            promptParams.requireAppointmentLink = {
                suggestedAppointmentLinkId: EncounterSuggestedAppointmentLinkId,
                patientContactId: patientContactId
            };
        }
        else if (cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote && !defaultInfo.appointmentId) {
            defaultInfo.appointmentId = EncounterSuggestedAppointmentLinkId;
        }

        // oasis class is required
        if (cacheStore.ClinicSettings.RequireOasisClassToSignNote && !promptParams.requireOasisDocumentClassLink) {
            promptParams.requireOasisDocumentClassLink = {
                suggestedOasisCode: sessionStore.lastSelectedNoteExternalCategory
            };
        }

        // skip popup
        if (!Object.keys(promptParams).any())
            return defaultInfo;

        // wait for user prompt to complete
        return await new Promise<AdditionalSignOffInformation>(
            (resolve, reject) => {
                // display myself in main. MainComponent will close it on resolve.
                sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayAdditionalSignOffInformationPopup, {
                    props: (
                        <LinkingPopup
                            requireAppointmentLink={promptParams.requireAppointmentLink}
                            requireOasisDocumentClassLink={promptParams.requireOasisDocumentClassLink}
                            submit={info => {
                                info.ValidationCallback = success => {
                                    if (!success)
                                        return;

                                    this.CloseLinkingPopup();
                                };
                                resolve(info);
                            }}
                            onCancel={() => reject()}
                        />
                    )
                });
            }
        );
    }

    public static async loadAppointments(
        patientId: string,
        ToDate: luxon.DateTime = null
    ): Promise<any> {
        return await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_History_GetAppointment,
            {
                PatientId: patientId,
                ToDate: ToDate
            }
        );
    }

    public static async linkNoteToAppointment(noteId: string, appointmentId: string): Promise<any> {
        return await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_LinkNoteToAppointment,
            {
                EncounterId: noteId,
                AppointmentId: appointmentId
            }
        );
    }

    public static CloseLinkingPopup(): void {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayAdditionalSignOffInformationPopup, { props: null });
    }
}

export { LinkingPopupService };
